import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import Phase1Summary from '@/pages/dashboard/components/Phase1Summary';
import HaftalikPerformans from '@/pages/dashboard/components/HaftalikPerformans';

function StatCardSkeleton() {
  const { isDark } = useTheme();
  return (
    <div className={`rounded-xl p-4 md:p-5 animate-pulse ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gray-200 dark:bg-gray-700" />
        <div className="w-12 h-6 rounded-full bg-gray-200 dark:bg-gray-700" />
      </div>
      <div className="mt-3 md:mt-4">
        <div className="w-16 h-8 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="w-24 h-4 rounded bg-gray-200 dark:bg-gray-700 mt-2" />
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  change,
  changeType,
  color,
}: {
  icon: string;
  label: string;
  value: string | number;
  change?: string;
  changeType?: 'up' | 'down' | 'neutral';
  color: string;
}) {
  const { isDark } = useTheme();

  return (
    <div
      className={`rounded-xl p-4 md:p-5 transition-all hover:shadow-soft ${
        isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center ${color}`}>
          <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
            <i className={`${icon} text-lg md:text-xl text-white`} />
          </div>
        </div>
        {change && (
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${
              changeType === 'up'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : changeType === 'down'
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
            }`}
          >
            {change}
          </span>
        )}
      </div>
      <div className="mt-3 md:mt-4">
        <p className={`text-2xl md:text-3xl font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          {value}
        </p>
        <p className={`text-xs md:text-sm mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          {label}
        </p>
      </div>
    </div>
  );
}

function QuickActionButton({
  icon,
  label,
  onClick,
  color,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  color: string;
}) {
  const { isDark } = useTheme();

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all hover:shadow-soft whitespace-nowrap ${
        isDark ? 'bg-dark-card border border-dark-border hover:bg-dark-border' : 'bg-white border border-light-border hover:bg-light-bg'
      }`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={`${icon} text-white`} />
        </div>
      </div>
      <span className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
        {label}
      </span>
    </button>
  );
}

function RecentListingRow({
  listing,
}: {
  listing: {
    id: string;
    status: string;
    ownerName: string;
    propertyType: string;
    location: string;
  };
}) {
  const { isDark } = useTheme();

  const statusColors: Record<string, string> = {
    Kabul: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    Düşünecek: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    Red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    'Başka Emlakçıda': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  };

  const dotColor =
    listing.status === 'Kabul'
      ? 'bg-green-500'
      : listing.status === 'Düşünecek'
      ? 'bg-yellow-500'
      : listing.status === 'Red'
      ? 'bg-red-500'
      : 'bg-gray-500';

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
        isDark ? 'hover:bg-dark-border' : 'hover:bg-light-bg'
      }`}
    >
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          {listing.ownerName}
        </p>
        <p className={`text-xs truncate ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          {listing.propertyType} · {listing.location}
        </p>
      </div>
      <span
        className={`text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 whitespace-nowrap ${
          statusColors[listing.status] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
        }`}
      >
        {listing.status}
      </span>
    </div>
  );
}

export default function Dashboard() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { stats, recentListings, todayTasks, toggleTask } = useDashboardStats();

  const pendingCount = todayTasks.filter((t) => !t.done).length;

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {stats.isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon="ri-map-pin-line"
              label="Bugün Eklenen FİZBO"
              value={stats.totalToday}
              change={`+${stats.totalToday}`}
              changeType="up"
              color="bg-primary-500"
            />
            <StatCard
              icon="ri-building-line"
              label="Aktif Portföy"
              value={stats.totalActivePortfolios}
              change="Aktif"
              changeType="up"
              color="bg-secondary-500"
            />
            <StatCard
              icon="ri-time-line"
              label="Bekleyen Dönüş"
              value={stats.pendingReturns}
              change={stats.pendingReturns > 0 ? `${stats.pendingReturns} acil` : 'Tamamlandı'}
              changeType={stats.pendingReturns > 0 ? 'neutral' : 'up'}
              color="bg-orange-500"
            />
            <StatCard
              icon="ri-phone-line"
              label="Bugünkü Arama"
              value={stats.todayCalls}
              change={`${stats.todayCalls} tamamlandı`}
              changeType="up"
              color="bg-blue-500"
            />
          </>
        )}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className={`text-base md:text-lg font-semibold mb-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          Hızlı Aksiyonlar
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <QuickActionButton
            icon="ri-add-line"
            label="Yeni FİZBO Ekle"
            onClick={() => navigate('/fizbo')}
            color="bg-primary-500"
          />
          <QuickActionButton
            icon="ri-add-line"
            label="Yeni Portföy Ekle"
            onClick={() => navigate('/portfoy')}
            color="bg-secondary-500"
          />
          <QuickActionButton
            icon="ri-search-line"
            label="Talep Ara"
            onClick={() => navigate('/talep-bul')}
            color="bg-blue-500"
          />
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Recent Listings */}
        <div
          className={`rounded-xl p-4 md:p-5 ${
            isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-base md:text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Son Eklenen İlanlar
            </h2>
            <button
              onClick={() => navigate('/fizbo')}
              className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
            >
              Tümünü Gör
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </div>
            </button>
          </div>
          <div className="space-y-1">
            {stats.isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="w-3/4 h-4 rounded bg-gray-200 dark:bg-gray-700" />
                    <div className="w-1/2 h-3 rounded bg-gray-200 dark:bg-gray-700" />
                  </div>
                </div>
              ))
            ) : recentListings.length > 0 ? (
              recentListings.map((listing) => (
                <RecentListingRow key={listing.id} listing={listing} />
              ))
            ) : (
              <div className={`text-sm text-center py-6 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Henüz FİZBO kaydı yok
              </div>
            )}
          </div>
        </div>

        {/* Today's Tasks */}
        <div
          className={`rounded-xl p-4 md:p-5 ${
            isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-base md:text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Bugünkü Görevler
            </h2>
            <span
              className={`text-xs font-medium px-2 py-1 rounded-full ${
                pendingCount > 0
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              }`}
            >
              {pendingCount} bekliyor
            </span>
          </div>
          <div className="space-y-3">
            {stats.isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
                  <div className="w-5 h-5 rounded-full border-2 border-gray-200 dark:border-gray-700 flex-shrink-0" />
                  <div className="flex-1 h-4 rounded bg-gray-200 dark:bg-gray-700" />
                  <div className="w-10 h-4 rounded bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                </div>
              ))
            ) : todayTasks.length > 0 ? (
              todayTasks.map((item) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-dark-border' : 'hover:bg-light-bg'
                  }`}
                >
                  <button
                    onClick={() => toggleTask(item.id, item.done)}
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${
                      item.done
                        ? 'bg-primary-500 border-primary-500'
                        : item.urgent
                        ? 'border-red-500 hover:border-red-400'
                        : isDark
                        ? 'border-dark-border hover:border-dark-text'
                        : 'border-light-border hover:border-light-text'
                    }`}
                  >
                    {item.done && <i className="ri-check-line text-white text-xs" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm ${item.done ? 'line-through opacity-50' : ''} ${
                        isDark ? 'text-dark-text' : 'text-light-text'
                      }`}
                    >
                      {item.task}
                    </p>
                  </div>
                  <span className={`text-xs flex-shrink-0 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                    {item.time}
                  </span>
                </div>
              ))
            ) : (
              <div className={`text-sm text-center py-6 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Bugün görev yok
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Haftalık Performans Grafiği */}
      <HaftalikPerformans />

      {/* Phase 1 Summary */}
      <Phase1Summary />

      {/* Conversion Rate */}
      <div
        className={`rounded-xl p-4 md:p-5 ${
          isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'
        }`}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-base md:text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            FİZBO Dönüşüm Oranı
          </h2>
          <span className="text-2xl font-bold text-primary-500">{stats.conversionRate}%</span>
        </div>
        <div className="w-full h-3 rounded-full bg-gray-200 dark:bg-dark-border overflow-hidden">
          <div
            className="h-full rounded-full bg-primary-500 transition-all duration-1000"
            style={{ width: `${stats.conversionRate}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-3 text-xs">
          <span className={isDark ? 'text-dark-muted' : 'text-light-muted'}>Hedef: 75%</span>
          <span className={isDark ? 'text-dark-muted' : 'text-light-muted'}>Son 30 gün</span>
        </div>
      </div>
    </div>
  );
}